import { memo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '~/components/ui/Collapsible';
import { Badge } from '~/components/ui/Badge';
import { formatDurationPrecise } from '~/lib/time';
import type { TestResultsState, TestSuite, TestCase, TestCaseStatus } from '~/types/context';

interface TestResultsProps {
  state: TestResultsState;
}

const STATUS_ICON: Record<TestCaseStatus, { icon: string; color: string }> = {
  passed: { icon: 'i-ph:check-circle', color: 'text-success' },
  failed: { icon: 'i-ph:x-circle', color: 'text-danger' },
  skipped: { icon: 'i-ph:minus-circle', color: 'text-warning' },
  running: { icon: 'i-svg-spinners:ring-resize', color: 'text-accent' },
};

export const TestResults = memo(({ state }: TestResultsProps) => {
  const [expanded, setExpanded] = useState(true);
  const { summary, suites, isRunning } = state;
  const percentage = summary.total > 0 ? Math.round((summary.passed / summary.total) * 100) : 0;

  const headerIcon = isRunning
    ? 'i-svg-spinners:ring-resize text-accent'
    : summary.failed > 0
      ? 'i-ph:x-circle text-danger'
      : 'i-ph:check-circle text-success';

  return (
    <div className="rounded-lg border border-hairline bg-surface overflow-hidden mb-3">
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-sunken transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <span className={`${headerIcon} text-base flex-shrink-0`} />
        <span className="text-sm font-medium text-primary">Test Results</span>

        <div className="flex items-center gap-1.5 ml-1">
          {summary.passed > 0 && (
            <Badge variant="success" size="sm">
              {summary.passed} passed
            </Badge>
          )}
          {summary.failed > 0 && (
            <Badge variant="danger" size="sm">
              {summary.failed} failed
            </Badge>
          )}
          {summary.skipped > 0 && (
            <Badge variant="warning" size="sm">
              {summary.skipped} skipped
            </Badge>
          )}
        </div>

        {summary.duration != null && (
          <span className="text-xs text-muted ml-auto mr-1">{formatDurationPrecise(summary.duration)}</span>
        )}

        <span
          className={`i-ph:caret-right text-xs text-muted ${summary.duration == null ? 'ml-auto' : ''} transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`}
        />
      </div>

      {/* Progress bar */}
      {summary.total > 0 && (
        <div className="px-3 py-1.5 border-t border-hairline">
          <div className="h-1.5 w-full rounded-full bg-sunken overflow-hidden flex">
            {summary.passed > 0 && (
              <div
                className="bg-success transition-all duration-300"
                style={{ width: `${(summary.passed / summary.total) * 100}%` }}
              />
            )}
            {summary.failed > 0 && (
              <div
                className="bg-danger transition-all duration-300"
                style={{ width: `${(summary.failed / summary.total) * 100}%` }}
              />
            )}
            {summary.skipped > 0 && (
              <div
                className="bg-warning transition-all duration-300"
                style={{ width: `${(summary.skipped / summary.total) * 100}%` }}
              />
            )}
          </div>
          <div className="flex justify-between text-xs text-muted mt-1">
            <span>
              {summary.passed}/{summary.total} tests passed
            </span>
            <span>{percentage}%</span>
          </div>
        </div>
      )}

      {/* Suite list */}
      <AnimatePresence initial={false}>
        {expanded && suites.length > 0 && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="border-t border-hairline px-3 py-1.5 space-y-1">
              {suites.map((suite) => (
                <TestSuiteRow key={suite.id} suite={suite} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

function TestSuiteRow({ suite }: { suite: TestSuite }) {
  const config = STATUS_ICON[suite.status] || STATUS_ICON.running;
  const passedCount = suite.tests.filter((t) => t.status === 'passed').length;
  const failedCount = suite.tests.filter((t) => t.status === 'failed').length;
  const skippedCount = suite.tests.filter((t) => t.status === 'skipped').length;
  const hasFailed = failedCount > 0;

  return (
    <Collapsible defaultOpen={hasFailed}>
      <div className="rounded-md border border-hairline overflow-hidden">
        <CollapsibleTrigger className="flex items-center gap-2 px-2.5 py-1.5 w-full hover:bg-sunken transition-colors group text-left">
          <span className={`${config.icon} ${config.color} text-sm flex-shrink-0`} />
          <span className="text-sm text-primary font-medium truncate">{suite.name}</span>

          <div className="flex items-center gap-2 text-xs ml-auto flex-shrink-0">
            {passedCount > 0 && <span className="text-success">{passedCount} passed</span>}
            {failedCount > 0 && <span className="text-danger">{failedCount} failed</span>}
            {skippedCount > 0 && <span className="text-warning">{skippedCount} skipped</span>}
            {suite.duration != null && <span className="text-muted">{formatDurationPrecise(suite.duration)}</span>}
            <span className="i-ph:caret-right text-xs text-muted transition-transform group-data-[state=open]:rotate-90" />
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-hairline divide-y divide-hairline">
            {suite.tests.map((test) => (
              <TestRow key={test.id} test={test} />
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function TestRow({ test }: { test: TestCase }) {
  const config = STATUS_ICON[test.status] || STATUS_ICON.running;
  const hasFailed = test.status === 'failed' && test.error;

  return (
    <div>
      <div className={`flex items-center gap-2 px-2.5 py-1.5 ${test.status === 'skipped' ? 'opacity-60' : ''}`}>
        <span className={`${config.icon} ${config.color} text-sm flex-shrink-0`} />
        <span className="text-sm text-primary truncate">{test.name}</span>
        {test.duration != null && (
          <span className="text-xs text-muted ml-auto flex-shrink-0">{formatDurationPrecise(test.duration)}</span>
        )}
      </div>

      {hasFailed && <TestErrorBlock message={test.error!.message} stack={test.error!.stack} />}
    </div>
  );
}

function TestErrorBlock({ message, stack }: { message: string; stack?: string }) {
  const [showStack, setShowStack] = useState(false);

  return (
    <div className="mx-2.5 mb-1.5 rounded-md bg-danger/5 border border-danger/10 p-2.5">
      <div className="text-xs text-danger font-medium">{message}</div>
      {stack && (
        <>
          <button
            className="text-xs text-muted hover:text-secondary mt-1 transition-colors"
            onClick={() => setShowStack(!showStack)}
          >
            {showStack ? 'Hide stack trace' : 'Show stack trace'}
          </button>
          {showStack && (
            <pre className="text-xs text-muted font-geist-mono whitespace-pre-wrap mt-1.5 max-h-48 overflow-y-auto">
              {stack}
            </pre>
          )}
        </>
      )}
    </div>
  );
}
